import React from "react";
import { Brain, TrendingUp, Activity, DollarSign, Info, X, RefreshCw } from "lucide-react";
import ModelInfoModal from "../ModelInfoModal";
import Header from "../Header";
import Footer from "../Footer";
import EosBackdrop from "../EosBackdrop";
import { useEOSModels } from "./useEOSModels";

const EOSModels: React.FC = () => {
  const {
    models,
    loading,
    error,
    successMessage,
    setSuccessMessage,
    accountBalance,
    infoModalOpen,
    selectedModelForInfo,
    fetchModels,
    handleApplyModel,
    handleUnapplyModel,
    handleOpenInfoModal,
    handleCloseInfoModal,
    formatPercentage,
    formatCurrency,
    hasSufficientBalance,
    getModelIcon,
    getModelIconColor,
  } = useEOSModels();

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
      <EosBackdrop />
      <Header />
      <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 py-8 px-4 sm:px-6 lg:px-8">
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="glass-card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Brain className="h-8 w-8 text-emerald-300" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-500">Total Models</p>
                  <p className="text-2xl font-bold text-slate-100">{models?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-green-300" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-500">Avg Success Rate</p>
                  <p className="text-2xl font-bold text-slate-100">
                    {models?.length > 0
                      ? formatPercentage(
                          models.reduce((sum, m) => sum + parseFloat(String(m.success_rate)), 0) / models.length,
                        )
                      : "0.0%"}
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="h-8 w-8 text-yellow-300" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-500">Avg Monthly Return</p>
                  <p className="text-2xl font-bold text-slate-100">
                    {models?.length > 0
                      ? formatPercentage(
                          models.reduce((sum, m) => sum + parseFloat(String(m.monthly_return)), 0) / models.length,
                        )
                      : "0.0%"}
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-8 w-8 text-green-300" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-500">Account Balance</p>
                  <p className="text-2xl font-bold text-slate-100">{formatCurrency(accountBalance)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-red-300" />
            </div>
            <h3 className="text-xl font-semibold text-slate-100 mb-2">Failed to Load Models</h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">{error}</p>
            <div className="flex justify-center">
              <button
                onClick={fetchModels}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ink-900 focus:ring-emerald-500 transition-colors duration-200"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Retry
              </button>
            </div>
          </div>
        )}

        {!error && (
          <>
            {successMessage && (
              <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-emerald-300" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-emerald-300">Success</h3>
                    <div className="mt-2 text-sm text-emerald-200">{successMessage}</div>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setSuccessMessage(null)}
                      className="inline-flex text-emerald-400 hover:text-emerald-200"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
                  <span className="ml-3 text-slate-400">Loading models...</span>
                </div>
              ) : !models || models.length === 0 ? (
                <div className="text-center py-12">
                  <Brain className="mx-auto h-16 w-16 text-accent-400" />
                  <h3 className="mt-4 text-xl font-semibold text-slate-100">No Trading Models Available</h3>
                  <p className="text-slate-400 max-w-md mx-auto">
                    Advanced algorithmic trading models will be available here.
                  </p>
                </div>
              ) : (
                <div className="glass-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-white/10">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Model
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Success Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Monthly Return
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Risk/Day
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Target/Day
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Required Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        {models &&
                          models.map((model) => {
                            const Icon = getModelIcon(model.type);
                            return (
                              <tr key={model.id || model._id} className="hover:bg-white/5">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <Icon className={`h-6 w-6 ${getModelIconColor(model.type)}`} />
                                    <div className="ml-3">
                                      <div className="flex items-center">
                                        <div className="text-sm font-medium text-slate-100">{model.name}</div>
                                        <button
                                          onClick={() => handleOpenInfoModal(model)}
                                          className="ml-2 text-slate-500 hover:text-accent-300 transition-colors"
                                          title="Get detailed model information"
                                        >
                                          <Info className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-green-300">
                                    {formatPercentage(model.success_rate)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-accent-300">
                                    {formatPercentage(model.monthly_return)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-red-300">{model.risk_per_day || 2.0}%</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-green-300">
                                    {model.target_per_day || 3.0}%
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-slate-100">
                                    {formatCurrency(model.required_amount)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex items-center space-x-3">
                                    <button
                                      className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm ${
                                        model.is_applied
                                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                          : hasSufficientBalance(model.required_amount)
                                            ? "bg-accent-500 text-white hover:bg-accent-600"
                                            : "bg-white/10 text-slate-500 cursor-not-allowed"
                                      }`}
                                      onClick={() =>
                                        model.is_applied
                                          ? handleUnapplyModel(model._id)
                                          : handleApplyModel(model._id)
                                      }
                                      disabled={!model.is_applied && !hasSufficientBalance(model.required_amount)}
                                      title={
                                        model.is_applied
                                          ? "Click to unapply this model"
                                          : hasSufficientBalance(model.required_amount)
                                            ? "Apply Model for Automated Trading"
                                            : `Insufficient balance. Required: ${formatCurrency(model.required_amount)}`
                                      }
                                    >
                                      {model.is_applied ? "Applied" : "Apply"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <ModelInfoModal isOpen={infoModalOpen} onClose={handleCloseInfoModal} model={selectedModelForInfo} />
      </div>
      <Footer />
    </div>
  );
};

export default EOSModels;
